import React, { PropsWithChildren, useCallback, useState } from 'react';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import Btn, { BtnModifier } from '../../../components/molecules/Btn';
import { Icons, SVG } from '../../../components/atoms/SVG';
import { AcceptDeclineForm } from '../../../_lib/Form2/standardForms/AcceptDeclineForm/AcceptDeclineForm';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { useDelayed } from '../../../hooks/useDelayed';
import { useInvalidateRefreshableBlock, useRefreshBlock } from '../../../_lib/BlockFetcher';
import { EnrichedAdditionalPrivilegeItem } from '../PrivilegeConfig.types';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import { BlockType } from '../../../types/api/ElementType';

type PrivilegeContainerProps = PropsWithChildren<{
  privilege: EnrichedAdditionalPrivilegeItem;
}>;

export function AdditionalPrivilegeContainer({ privilege, children }: PrivilegeContainerProps) {
  const { t } = useSonTranslation('PrivilegeConfig');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { refresh } = useRefreshBlock();
  const closeModal = useCallback(() => {
    setShowDeleteModal(false);
  }, []);
  const invalidateRefreshableBlock = useInvalidateRefreshableBlock();

  const refreshAndClose = useCallback(() => {
    refresh();
    // we also want to refresh the basic privileges block.
    invalidateRefreshableBlock(BlockType.basicprivilegeconfig);
    closeModal();
  }, [closeModal, refresh, invalidateRefreshableBlock]);
  const delayedRefreshAndClose = useDelayed(refreshAndClose);

  return (
    <div className="privilege-config__additional-privilege-container">
      <div className="privilege-config__related-icon">
        <SVG svgSpriteId={Icons.PAGE_TO_PAGE_ROLE_RELATED} />
      </div>
      <div className="privilege-config__block privilege-config__block--no-border">
        <div className="privilege-config__block-headline-container">
          { /* this is deliberately not a semantic headline because of list semantics conflicting  */ }
          <p className="privilege-config__block-headline">
            {privilege.label}
          </p>
          {children}
        </div>
        <div className="privilege-config__block-buttons">
          <Btn
            svgSpriteId={Icons.DELETE_TRASHCAN}
            modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
            hiddenText
            onClick={() => setShowDeleteModal(true)}
            titleText={t('actions.delete.headline', {
              name: privilege.label,
            })}
            text={t('actions.delete.headline', {
              name: privilege.label,
            })}
            tooltipPlacement={TooltipPlacement.TOP}
            ariaHaspopup
          />
        </div>
      </div>
      {showDeleteModal
        ? (
            <ModalDialog
              closeModal={() => setShowDeleteModal(false)}
              headline={t('actions.delete.headline', {
                name: privilege.label,
              })}
              initialFocusIndex={1}
            >
              <AcceptDeclineForm
                messageSuccess={t('actions.delete.status.success')}
                messageSending={t('actions.delete.status.sending')}
                messageFail={t('actions.delete.status.fail')}
                headline={t('actions.delete.question')}
                form={privilege.delete}
                onSuccess={delayedRefreshAndClose}
              />
            </ModalDialog>
          )
        : null}
    </div>
  );
}
